import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import type { Organization, RoleInfo, User } from "@/types";

interface EditUserDialogProps {
  user: User;
  assignable: RoleInfo[];
  isSuperAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function EditUserDialog({ user, assignable, isSuperAdmin, onClose, onSaved }: EditUserDialogProps) {
  const [username, setUsername] = useState(user.username);
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user.role);
  const [orgId, setOrgId] = useState<number | null>(user.org_id);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: orgsData } = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: () => api.listOrganizations(),
    enabled: isSuperAdmin,
  });
  const orgs: Organization[] = orgsData?.organizations ?? [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const body: { username?: string; role?: string; password?: string; org_id?: number | null } = {};
      if (username.trim() !== user.username) body.username = username.trim();
      if (role !== user.role) body.role = role;
      if (password) body.password = password;
      if (isSuperAdmin && orgId !== user.org_id) body.org_id = orgId;
      if (Object.keys(body).length === 0) { onClose(); return; }
      await api.updateUser(user.id, body);
      onSaved();
    } catch (err) {
      setError((err as Error).message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="w-full max-w-sm rounded-card bg-white p-6 shadow-card" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-4 text-lg font-semibold text-near-black">Edit user</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Organization</label>
              <select value={orgId ?? ""} onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : null)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus">
                <option value="">None</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Username</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" autoFocus required minLength={2} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">New password <span className="font-normal normal-case text-text-secondary">(leave blank to keep current)</span></label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus" minLength={4} placeholder="••••••••" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus">
              {assignable.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
            </select>
          </div>
          {error && <p className="rounded-badge bg-rose-100 px-3 py-2 text-xs font-medium text-error-text">{error}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-btn px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:text-near-black">Cancel</button>
            <button type="submit" disabled={saving} className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50">{saving ? "Saving..." : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
